import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { COLORS, SPACING, RADIUS, Z_INDEX, TYPOGRAPHY } from '../design/tokens';
import { MetricChart, getMetricConfig, MetricType } from './ui';
import { apiClient } from '../api/apiConfig';

// Helper to map metricKey to MetricType
const getMetricTypeFromKey = (key: string): MetricType => {
  const mapping: Record<string, MetricType> = {
    'resting_hr': 'heart_rate',
    'heart_rate_resting': 'heart_rate',
    'hrv_rmssd': 'hrv',
    'hrv': 'hrv',
    'body_weight_kg': 'weight',
    'weight': 'weight',
    'vo2_max': 'vo2_max',
    'steps': 'steps',
    'sleep_duration_minutes': 'sleep_minutes',
    'sleep_debt_minutes': 'sleep_minutes',
    'sleep_hours': 'sleep',
    'readiness_score': 'score',
    'sleep_score': 'score',
  };
  return mapping[key] || 'generic';
};

// Helper to determine chart type - steps and sleep hours use bars, others use lines
const getChartTypeForMetric = (key: string): 'bar' | 'line' => {
  const barMetrics = ['steps', 'sleep_hours', 'sleep_duration_minutes', 'stress_time_minutes'];
  return barMetrics.includes(key) ? 'bar' : 'line';
};

// Map metricKey to HealthGoal metric_type
const getGoalMetricType = (key: string): string => {
  const mapping: Record<string, string> = {
    'resting_hr': 'heart_rate_resting',
    'heart_rate_resting': 'heart_rate_resting',
    'hrv_rmssd': 'hrv',
    'hrv': 'hrv',
    'body_weight_kg': 'weight',
    'weight': 'weight',
    'vo2_max': 'vo2_max',
    'steps': 'steps',
    'sleep_duration_minutes': 'sleep_hours',
    'sleep_hours': 'sleep_hours',
    'readiness_score': 'readiness_score',
    'sleep_score': 'sleep_quality',
  };
  return mapping[key] || key;
};

interface HealthGoal {
  id: number;
  name: string;
  metric_type: string;
  start_value: number | null;
  target_value: number;
  start_date: string;
  end_date: string | null;
  status: string;
}

interface SecondaryMetric {
  key: string;
  label: string;
  color: string;
  unit?: string;
}

interface MetricHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  metricKey: string;
  data: any[];
  unit?: string;
  color?: string;
  targetValue?: number;
  autoScaleY?: boolean;
  secondaryMetric?: SecondaryMetric;
  valueLabels?: Record<number, string>;
}

const MetricHistoryModal: React.FC<MetricHistoryModalProps> = ({
  isOpen,
  onClose,
  title,
  metricKey,
  data,
  unit = '',
  color = COLORS.accent.default,
  targetValue,
  autoScaleY = false,
  secondaryMetric,
  valueLabels
}) => {
  const [goals, setGoals] = useState<HealthGoal[]>([]);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState<HealthGoal | null>(null);
  const [goalForm, setGoalForm] = useState({
    name: '',
    start_value: '',
    target_value: '',
    start_date: '',
    end_date: '',
  });

  // Get metric config for formatting
  const metricType = getMetricTypeFromKey(metricKey);
  const metricConfig = getMetricConfig(metricType);
  const goalMetricType = getGoalMetricType(metricKey);

  // Fetch goals for this metric
  useEffect(() => {
    if (isOpen) {
      fetchGoals();
    }
  }, [isOpen, metricKey]);

  const fetchGoals = async () => {
    try {
      const response = await apiClient.get('/api/health-goals/', {
        params: { metric_type: goalMetricType, status: 'active' }
      });
      setGoals(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error fetching goals:', err);
    }
  };

  const handleSaveGoal = async () => {
    try {
      const payload = {
        name: goalForm.name || `${title} Goal`,
        metric_type: goalMetricType,
        start_value: goalForm.start_value ? parseFloat(goalForm.start_value) : null,
        target_value: parseFloat(goalForm.target_value),
        start_date: goalForm.start_date,
        end_date: goalForm.end_date || null,
        frequency: 'daily',
        comparison: 'gte',
      };

      if (editingGoal) {
        await apiClient.patch(`/api/health-goals/${editingGoal.id}/`, payload);
      } else {
        await apiClient.post('/api/health-goals/', payload);
      }

      setShowGoalForm(false);
      setEditingGoal(null);
      setGoalForm({ name: '', start_value: '', target_value: '', start_date: '', end_date: '' });
      fetchGoals();
    } catch (err) {
      console.error('Error saving goal:', err);
    }
  };

  const handleDeleteGoal = async (goalId: number) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await apiClient.delete(`/api/health-goals/${goalId}/`);
      fetchGoals();
    } catch (err) {
      console.error('Error deleting goal:', err);
    }
  };

  const handleEditGoal = (goal: HealthGoal) => {
    setEditingGoal(goal);
    setGoalForm({
      name: goal.name,
      start_value: goal.start_value?.toString() || '',
      target_value: goal.target_value.toString(),
      start_date: goal.start_date,
      end_date: goal.end_date || '',
    });
    setShowGoalForm(true);
  };

  // Guard against undefined/null/non-array data
  const safeData = Array.isArray(data) ? data : [];

  // Deduplicate by date
  const seenDates = new Set<string>();
  const chartData = safeData
    .filter(d => {
      if (!d || typeof d !== 'object') return false;
      if (!d.date) return false;
      if (d[metricKey] === null || d[metricKey] === undefined) return false;
      if (seenDates.has(d.date)) return false;

      const parsedValue = parseFloat(d[metricKey]);
      if (Number.isNaN(parsedValue)) return false;

      seenDates.add(d.date);

      if (metricKey === 'steps' && parsedValue > 50000) {
        return false;
      }

      return true;
    })
    .map(d => {
      const secondaryParsed = secondaryMetric && d[secondaryMetric.key] !== null && d[secondaryMetric.key] !== undefined
        ? parseFloat(d[secondaryMetric.key])
        : null;

      const [year, month, day] = d.date.split('-').map(Number);
      const localDate = new Date(year, month - 1, day);

      return {
        date: localDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: parseFloat(d[metricKey]),
        fullDate: d.date,
        ...(secondaryParsed !== null && !Number.isNaN(secondaryParsed)
          ? { secondaryValue: secondaryParsed }
          : {})
      };
    })
    .reverse();

  // Add goal line data points if goal exists
  const activeGoal = goals.find(g => g.status === 'active') || goals[0];
  let chartDataWithGoal = chartData;

  if (activeGoal && activeGoal.target_value !== null && activeGoal.target_value !== undefined) {
    const hasStartValue = activeGoal.start_value !== null && activeGoal.start_value !== undefined;
    const hasEndDate = !!activeGoal.end_date;

    if (hasStartValue && hasEndDate) {
      // Progressive goal line from start_value to target_value
      const startDate = new Date(activeGoal.start_date);
      const endDate = new Date(activeGoal.end_date!);
      const totalDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
      const startVal = Number(activeGoal.start_value);
      const targetVal = Number(activeGoal.target_value);
      const valueChange = targetVal - startVal;

      chartDataWithGoal = chartData.map(d => {
        const [year, month, day] = d.fullDate.split('-').map(Number);
        const pointDate = new Date(year, month - 1, day);
        const daysFromStart = (pointDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);

        let goalValue: number | null = null;
        if (daysFromStart >= 0 && daysFromStart <= totalDays) {
          const progress = daysFromStart / totalDays;
          goalValue = startVal + (valueChange * progress);
        } else if (daysFromStart > totalDays) {
          goalValue = targetVal;
        }

        return {
          ...d,
          goalLine: goalValue
        };
      });
    } else {
      // Horizontal line at target value (when no start_value or end_date)
      const targetVal = Number(activeGoal.target_value);
      chartDataWithGoal = chartData.map(d => ({
        ...d,
        goalLine: targetVal
      }));
    }
  }

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: Z_INDEX.modal }} onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }} />
        </Transition.Child>

        <div style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[16]
          }}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel style={{
                width: '100%',
                maxWidth: '900px',
                borderRadius: RADIUS,
                backgroundColor: COLORS.bg.panel,
                padding: SPACING[24],
                textAlign: 'left',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                border: `1px solid ${COLORS.border.default}`
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: SPACING[24]
                }}>
                  <Dialog.Title style={{
                    fontSize: TYPOGRAPHY.size['3xl'],
                    fontWeight: 600,
                    color: COLORS.text.primary
                  }}>
                    History: {title}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: COLORS.text.secondary,
                      padding: SPACING[8]
                    }}
                  >
                    <XMarkIcon style={{ width: 24, height: 24 }} />
                  </button>
                </div>

                {/* Stats Row */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: SPACING[16],
                  marginBottom: SPACING[24]
                }}>
                  <div>
                    <div style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.tertiary, marginBottom: SPACING[4] }}>
                      LATEST
                    </div>
                    <div style={{ fontSize: TYPOGRAPHY.size['3xl'], fontWeight: 600, color: COLORS.text.primary }}>
                      {chartData.length > 0
                        ? (valueLabels && valueLabels[chartData[chartData.length - 1].value] !== undefined
                          ? valueLabels[chartData[chartData.length - 1].value]
                          : `${metricConfig.formatter(chartData[chartData.length - 1].value)} ${metricConfig.unit || unit}`)
                        : '-'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.tertiary, marginBottom: SPACING[4] }}>
                      {valueLabels ? 'DISTRIBUTION' : 'AVERAGE'}
                    </div>
                    <div style={{ fontSize: valueLabels ? TYPOGRAPHY.size.lg : TYPOGRAPHY.size['3xl'], fontWeight: 600, color: COLORS.text.primary }}>
                      {chartData.length > 0
                        ? (valueLabels
                          ? Object.entries(
                              chartData.reduce((acc, d) => {
                                const label = valueLabels[d.value] || String(d.value);
                                acc[label] = (acc[label] || 0) + 1;
                                return acc;
                              }, {} as Record<string, number>)
                            ).map(([label, count]) => `${label}: ${count}`).join(', ')
                          : `${metricConfig.formatter(chartData.reduce((sum, d) => sum + d.value, 0) / chartData.length)} ${metricConfig.unit || unit}`)
                        : '-'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.tertiary, marginBottom: SPACING[4] }}>
                      DATA POINTS
                    </div>
                    <div style={{ fontSize: TYPOGRAPHY.size['3xl'], fontWeight: 600, color: COLORS.text.primary }}>
                      {chartData.length}
                    </div>
                  </div>
                </div>

                {/* Goal Section */}
                <div style={{
                  marginBottom: SPACING[16],
                  padding: SPACING[16],
                  backgroundColor: COLORS.bg.hover,
                  borderRadius: RADIUS,
                  border: `1px solid ${COLORS.border.default}`
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: goals.length > 0 || showGoalForm ? SPACING[12] : 0
                  }}>
                    <span style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: 600, color: COLORS.text.secondary }}>
                      GOAL
                    </span>
                    {!showGoalForm && (
                      <button
                        onClick={() => {
                          setEditingGoal(null);
                          setGoalForm({
                            name: '',
                            start_value: chartData.length > 0 ? chartData[chartData.length - 1].value.toString() : '',
                            target_value: '',
                            start_date: new Date().toISOString().split('T')[0],
                            end_date: '',
                          });
                          setShowGoalForm(true);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: SPACING[4],
                          background: 'none',
                          border: 'none',
                          color: COLORS.accent.default,
                          cursor: 'pointer',
                          fontSize: TYPOGRAPHY.size.sm,
                          fontWeight: 500
                        }}
                      >
                        <PlusIcon style={{ width: 16, height: 16 }} />
                        Add Goal
                      </button>
                    )}
                  </div>

                  {/* Existing Goals */}
                  {goals.map(goal => (
                    <div key={goal.id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: SPACING[8],
                      backgroundColor: COLORS.bg.panel,
                      borderRadius: RADIUS,
                      marginBottom: SPACING[8]
                    }}>
                      <div>
                        <div style={{ fontWeight: 500, color: COLORS.text.primary }}>{goal.name}</div>
                        <div style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.tertiary }}>
                          {goal.start_value !== null && `${metricConfig.formatter(goal.start_value)} → `}
                          {metricConfig.formatter(goal.target_value)} {metricConfig.unit}
                          {goal.end_date && ` by ${new Date(goal.end_date).toLocaleDateString()}`}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: SPACING[8] }}>
                        <button
                          onClick={() => handleEditGoal(goal)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.text.tertiary }}
                        >
                          <PencilIcon style={{ width: 16, height: 16 }} />
                        </button>
                        <button
                          onClick={() => handleDeleteGoal(goal.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.status.error }}
                        >
                          <TrashIcon style={{ width: 16, height: 16 }} />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Goal Form */}
                  {showGoalForm && (
                    <div style={{
                      padding: SPACING[16],
                      backgroundColor: COLORS.bg.panel,
                      borderRadius: RADIUS,
                      border: `1px solid ${COLORS.border.default}`
                    }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING[12], marginBottom: SPACING[12] }}>
                        <div>
                          <label style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, display: 'block', marginBottom: SPACING[4] }}>
                            Goal Name
                          </label>
                          <input
                            type="text"
                            value={goalForm.name}
                            onChange={e => setGoalForm({ ...goalForm, name: e.target.value })}
                            placeholder={`${title} Goal`}
                            style={{
                              width: '100%',
                              padding: SPACING[8],
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              backgroundColor: COLORS.bg.base,
                              color: COLORS.text.primary,
                              fontSize: TYPOGRAPHY.size.md
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, display: 'block', marginBottom: SPACING[4] }}>
                            Start Value
                          </label>
                          <input
                            type="number"
                            value={goalForm.start_value}
                            onChange={e => setGoalForm({ ...goalForm, start_value: e.target.value })}
                            placeholder="Current value"
                            style={{
                              width: '100%',
                              padding: SPACING[8],
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              backgroundColor: COLORS.bg.base,
                              color: COLORS.text.primary,
                              fontSize: TYPOGRAPHY.size.md
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, display: 'block', marginBottom: SPACING[4] }}>
                            Target Value *
                          </label>
                          <input
                            type="number"
                            value={goalForm.target_value}
                            onChange={e => setGoalForm({ ...goalForm, target_value: e.target.value })}
                            placeholder="Target"
                            required
                            style={{
                              width: '100%',
                              padding: SPACING[8],
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              backgroundColor: COLORS.bg.base,
                              color: COLORS.text.primary,
                              fontSize: TYPOGRAPHY.size.md
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, display: 'block', marginBottom: SPACING[4] }}>
                            Start Date *
                          </label>
                          <input
                            type="date"
                            value={goalForm.start_date}
                            onChange={e => setGoalForm({ ...goalForm, start_date: e.target.value })}
                            required
                            style={{
                              width: '100%',
                              padding: SPACING[8],
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              backgroundColor: COLORS.bg.base,
                              color: COLORS.text.primary,
                              fontSize: TYPOGRAPHY.size.md
                            }}
                          />
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                          <label style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, display: 'block', marginBottom: SPACING[4] }}>
                            End Date (for goal line)
                          </label>
                          <input
                            type="date"
                            value={goalForm.end_date}
                            onChange={e => setGoalForm({ ...goalForm, end_date: e.target.value })}
                            style={{
                              width: '100%',
                              padding: SPACING[8],
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              backgroundColor: COLORS.bg.base,
                              color: COLORS.text.primary,
                              fontSize: TYPOGRAPHY.size.md
                            }}
                          />
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: SPACING[8] }}>
                        <button
                          onClick={() => {
                            setShowGoalForm(false);
                            setEditingGoal(null);
                          }}
                          style={{
                            padding: `${SPACING[8]}px ${SPACING[16]}px`,
                            borderRadius: RADIUS,
                            border: `1px solid ${COLORS.border.default}`,
                            backgroundColor: 'transparent',
                            color: COLORS.text.secondary,
                            cursor: 'pointer',
                            fontSize: TYPOGRAPHY.size.sm
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveGoal}
                          disabled={!goalForm.target_value || !goalForm.start_date}
                          style={{
                            padding: `${SPACING[8]}px ${SPACING[16]}px`,
                            borderRadius: RADIUS,
                            border: 'none',
                            backgroundColor: COLORS.accent.default,
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: TYPOGRAPHY.size.sm,
                            fontWeight: 500,
                            opacity: (!goalForm.target_value || !goalForm.start_date) ? 0.5 : 1
                          }}
                        >
                          {editingGoal ? 'Update' : 'Save'} Goal
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Chart */}
                {chartDataWithGoal.length > 0 ? (
                  <MetricChart
                    data={chartDataWithGoal}
                    series={[
                      {
                        dataKey: 'value',
                        name: title,
                        metricType: metricType,
                        color: color
                      },
                      ...(activeGoal ? [{
                        dataKey: 'goalLine',
                        name: 'Goal',
                        metricType: metricType,
                        color: COLORS.status.success,
                        strokeDasharray: '5 5',
                        chartType: 'line' as const  // Goal line always renders as line
                      }] : []),
                      ...(secondaryMetric ? [{
                        dataKey: 'secondaryValue',
                        name: secondaryMetric.label,
                        metricType: getMetricTypeFromKey(secondaryMetric.key),
                        color: secondaryMetric.color,
                        ...(getChartTypeForMetric(metricKey) === 'bar' ? {} : { chartType: 'line' as const, hideDots: true })
                      }] : [])
                    ]}
                    type={getChartTypeForMetric(metricKey)}
                    height={400}
                    targetValue={targetValue}
                    targetLabel="Target"
                  />
                ) : (
                  <div style={{
                    height: '400px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: COLORS.text.tertiary
                  }}>
                    No historical data available
                  </div>
                )}

                {/* Legend */}
                {(secondaryMetric || activeGoal) && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: SPACING[24],
                    marginTop: SPACING[16]
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                      <div style={{
                        width: 24,
                        height: 3,
                        backgroundColor: color,
                        borderRadius: RADIUS
                      }} />
                      <span style={{ fontSize: TYPOGRAPHY.size.md, color: COLORS.text.secondary }}>{title}</span>
                    </div>
                    {activeGoal && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                        <div style={{
                          width: 24,
                          height: 3,
                          backgroundColor: COLORS.status.success,
                          borderRadius: RADIUS,
                          backgroundImage: `repeating-linear-gradient(90deg, ${COLORS.status.success} 0, ${COLORS.status.success} 5px, transparent 5px, transparent 10px)`
                        }} />
                        <span style={{ fontSize: TYPOGRAPHY.size.md, color: COLORS.text.secondary }}>Goal Line</span>
                      </div>
                    )}
                    {secondaryMetric && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                        <div style={{
                          width: 24,
                          height: 3,
                          backgroundColor: secondaryMetric.color,
                          borderRadius: RADIUS,
                          backgroundImage: `repeating-linear-gradient(90deg, ${secondaryMetric.color} 0, ${secondaryMetric.color} 5px, transparent 5px, transparent 10px)`
                        }} />
                        <span style={{ fontSize: TYPOGRAPHY.size.md, color: COLORS.text.secondary }}>{secondaryMetric.label}</span>
                      </div>
                    )}
                  </div>
                )}

                <div style={{ marginTop: SPACING[24], textAlign: 'right' }}>
                  <button
                    onClick={onClose}
                    style={{
                      background: COLORS.accent.default,
                      color: 'white',
                      border: 'none',
                      borderRadius: RADIUS,
                      padding: `${SPACING[12]}px ${SPACING[24]}px`,
                      fontSize: TYPOGRAPHY.size.lg,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MetricHistoryModal;
